import { getPageConfig } from '@/lib/aggregation';
import { getIsSiteWideJackpotEnabled } from '@/logic/page-config/static-page/static-page-utils';
import type {
    NavigationItem,
    NavigationItemExternalLink,
    NavigationItemLink,
    NavigationItemLinkType
} from '@/types/aggregation/common';
import { ROUTES } from '@/utils/constants';
import { removeProductFromUid } from '@/utils/url/url';
import { LINK_TYPE_CATEGORY } from '@/utils/constants/navigation';

export type NavigationItemUI = {
    name: string;
    pathname: string | null;
    customImage: {
        name: string;
        url: string;
    };
    customImageActive: {
        name: string;
        url: string;
    };
    type?: string;
    linkUID?: string;
    linkType?: NavigationItemLinkType;
    isHighlighted?: boolean;
    newItemsCount?: number;
    pinned: boolean;
    isPersonalized: boolean;
};

export const getPathFromLink = (link: NavigationItemLink) => {
    switch (link.type) {
        case 'static_page':
            return `${ROUTES.STATIC_PAGE}${removeProductFromUid(link.uid)}`;
        case 'category':
            return `${ROUTES.CATEGORY}${removeProductFromUid(link.uid)}`;
        case 'homepage':
            return ROUTES.HOMEPAGE;
        case 'game':
        case 'game_lobby':
            return `${ROUTES.GAME}${link.uid}`;
        case 'dynamic_route':
            return `${ROUTES.DYNAMIC_PAGE}${removeProductFromUid(link.uid)}`;
        default:
            return null;
    }
};

export const getPathFromExternalLink = (external_link: NavigationItemExternalLink) => {
    switch (external_link.link_type) {
        case 'Document':
            return getPathFromLink(external_link);
        case 'Web':
            return external_link.url;
        case 'Media':
            return external_link.url;
        default:
            return null;
    }
};

export const getPathFromNavigationItem = (item: NavigationItem) => {
    const pathName = getPathFromLink(item.link);
    return pathName || getPathFromExternalLink(item.external_link);
};

const mapNavigationItem = (items: NavigationItem[], personalizedCategories: Set<string>): NavigationItemUI[] =>
    items
        .map((item: NavigationItem) => ({
            name: item.name,
            pathname: getPathFromNavigationItem(item),
            linkUID: item?.link?.uid,
            linkType: item?.link?.type,
            isHighlighted: item?.is_highlighted,
            type: item?.item_type,
            customImage: {
                name: item.custom_image.name,
                url: item.custom_image.url
            },
            customImageActive: {
                name: item.custom_image_active.name,
                url: item.custom_image_active.url
            },
            newItemsCount: item.new_items_count,
            pinned: !!item.pinned,
            isPersonalized:
                item?.link?.type === LINK_TYPE_CATEGORY &&
                !!item?.link?.uid &&
                personalizedCategories.has(removeProductFromUid(item.link.uid))
        }))
        .filter((link) => link.pathname)
        .filter((link, index, self) => self.findIndex((l) => l.pathname === link.pathname) === index);

/**
 * When jackpot is disabled, filters out SWJ categories from top nav.
 */
const filterSwjCategoryFromTopNav = (
    items: NavigationItemUI[],
    jackpotCategoryUids: Set<string>
): NavigationItemUI[] => {
    if (jackpotCategoryUids.size === 0) return items;

    return items.filter((item) => {
        if (item.linkType !== 'category' || !item.linkUID) return true;
        const categorySlug = removeProductFromUid(item.linkUID);
        return !jackpotCategoryUids.has(categorySlug);
    });
};

export const getTopNav = async ({ currentRoute }: { currentRoute: string }): Promise<NavigationItemUI[]> => {
    const json = await getPageConfig(currentRoute);

    if (!json?.pageConfig?.product?.data?.top_navigation?.data?.items?.length) {
        return [];
    }

    const personalizedCategories = new Set(
        (json.pageConfig.categoryRankings?.categories ?? []).map((c: { category_name: string }) => c.category_name)
    );
    const items = mapNavigationItem(json.pageConfig.product.data.top_navigation.data.items, personalizedCategories);

    if (!getIsSiteWideJackpotEnabled(json.pageConfig.product)) {
        const jackpotCategories = json.pageConfig.product.data?.site_wide_jackpot?.jackpot_categories ?? [];
        const jackpotCategoryUids = new Set(
            jackpotCategories
                .map((item: { category?: { uid?: string }; uid?: string }) => item.category?.uid ?? item.uid)
                .filter((uid): uid is string => Boolean(uid))
                .map((uid) => removeProductFromUid(uid))
        );
        return filterSwjCategoryFromTopNav(items, jackpotCategoryUids);
    }

    return items;
};

export const getBottomNav = async ({ currentRoute }: { currentRoute: string }): Promise<NavigationItemUI[] | null> => {
    const json = await getPageConfig(currentRoute);

    if (!json?.pageConfig?.product?.data?.bottom_bar?.data?.items) {
        return null;
    }

    return mapNavigationItem(json.pageConfig.product.data.bottom_bar.data.items, new Set());
};
